import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const PortalAuthContext = createContext();

export const usePortalAuth = () => {
  const context = useContext(PortalAuthContext);
  if (!context) {
    throw new Error('usePortalAuth must be used within a PortalAuthProvider');
  }
  return context;
};

export const PortalAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('portalToken');
    if (token) {
      loadCustomer(token);
    } else {
      setLoading(false);
    }
  }, []);

  const loadCustomer = async (token) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/portal/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomer(response.data);
    } catch (err) {
      console.error('Failed to load portal customer:', err);
      localStorage.removeItem('portalToken');
      setCustomer(null);
    } finally {
      setLoading(false);
    }
  };

  // Login with email and password
  const login = async (email, password) => {
    setError(null);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/portal/login`, {
        email,
        password
      });
      const { token, customer: customerData } = response.data;

      localStorage.setItem('portalToken', token);
      setCustomer(customerData);

      return { success: true, customer: customerData };
    } catch (err) {
      const message = err.response?.data?.error || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Register with invite token and password
  const register = async (token, password) => {
    setError(null);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/portal/register`, {
        token,
        password
      });
      const { token: jwtToken, customer: customerData } = response.data;

      localStorage.setItem('portalToken', jwtToken);
      setCustomer(customerData);

      return { success: true, customer: customerData };
    } catch (err) {
      const message = err.response?.data?.error || 'Registration failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Request password reset
  const forgotPassword = async (email) => {
    setError(null);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/portal/forgot-password`, {
        email
      });
      return { success: true, message: response.data.message };
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to process request';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Reset password with token
  const resetPassword = async (token, password) => {
    setError(null);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/portal/reset-password`, {
        token,
        password
      });
      return { success: true, message: response.data.message };
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to reset password';
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('portalToken');
    setCustomer(null);
  };

  const getAuthHeader = () => {
    const token = localStorage.getItem('portalToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const value = {
    customer,
    loading,
    error,
    login,
    register,
    forgotPassword,
    resetPassword,
    logout,
    getAuthHeader,
    isAuthenticated: !!customer,
    refreshCustomer: () => {
      const token = localStorage.getItem('portalToken');
      if (token) loadCustomer(token);
    }
  };

  return (
    <PortalAuthContext.Provider value={value}>
      {children}
    </PortalAuthContext.Provider>
  );
};
